import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  // Intro animation state
  const [showIntro, setShowIntro] = useState(true);
  const [compilationStep, setCompilationStep] = useState(0);
  const [compilationProgress, setCompilationProgress] = useState(0);
  
  const [showPolitorium, setShowPolitorium] = useState(false);
  const [showLifework, setShowLifework] = useState(false);
  const [showHobbyist, setShowHobbyist] = useState(false);
  const [showAspirantPrep, setShowAspirantPrep] = useState(false);
  const [showAlshifa, setShowAlshifa] = useState(false);
  const [showGymTool, setShowGymTool] = useState(false);
  const [showSpectraXStream, setShowSpectraXStream] = useState(false);
  const [showErpTool, setShowErpTool] = useState(false);
  const [showKiranaTool, setShowKiranaTool] = useState(false);
  const [showApiHandshaking, setShowApiHandshaking] = useState(false);
  const [showFraserYacht, setShowFraserYacht] = useState(false);
  const [showLuxuryPresence, setShowLuxuryPresence] = useState(false);
  const [showTravelot, setShowTravelot] = useState(false);
  const [showExamPrep, setShowExamPrep] = useState(false);
  const [showMaldivesGuide, setShowMaldivesGuide] = useState(false);
  const [showShoppingApp, setShowShoppingApp] = useState(false);
  
  // Terminal window controls
  const [terminalState, setTerminalState] = useState('normal'); // 'normal', 'minimized', 'maximized', 'closed'
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Settings menu
  const [showSettings, setShowSettings] = useState(false);
  const [fontSize, setFontSize] = useState('normal'); // 'small', 'normal', 'large'
  const [terminalTheme, setTerminalTheme] = useState('dark'); // 'dark', 'matrix', 'retro'
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showScanlines, setShowScanlines] = useState(false);

  // Testimonials carousel
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  // Live availability status
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const testimonials = [
    {
      name: "Thomas Moore",
      role: "CEO",
      company: "Politorium",
      project: "Political Platform App",
      rating: 5,
      text: "Lohit transformed our vision into a stunning iOS app. His expertise in React Native and attention to user experience made Politorium a success in the App Store.",
      avatar: "🧑‍💼"
    },
    {
      name: "Jay Runner", 
      role: "CEO",
      company: "LifeWork Technologies",
      project: "AI-Powered Growth Platform",
      rating: 5,
      text: "Working with Lohit was exceptional. He delivered our AI-driven platform ahead of schedule with clean, scalable code. Highly recommended for complex projects.",
      avatar: "👩‍💼"
    },
    {
      name: "ELizabeth Liz",
      role: "CEO",
      company: "Hobbyist Co",
      project: "Events Platform Development",
      rating: 5,
      text: "Lohit's full-stack development skills are outstanding. Our events platform handles thousands of users seamlessly. Professional, reliable, and skilled.",
      avatar: "👨‍💻"
    },
    {
      name: "Dr.Amenta",
      role: "CEO",
      company: "Alshifa Wellness",
      project: "Mental Health App",
      rating: 5,
      text: "Lohit understood our unique requirements for faith-based wellness. He delivered a beautiful, culturally sensitive app that our community loves.",
      avatar: "👨‍⚕️"
    },
    {
      name: "Priya Sharma",
      role: "Business Owner",
      company: "FitZone Gym",
      project: "Gym Management ERP",
      rating: 5,
      text: "The gym ERP system Lohit built has revolutionized our operations. Biometric integration, mobile apps - everything works flawlessly. ROI in 3 months!",
      avatar: "💪"
    },
    {
      name: "Ahmed Nashith",
      role: "Resort Manager", 
      company: "SpectraXStream",
      project: "ELV Infrastructure",
      rating: 5,
      text: "Lohit's work on our resort's smart infrastructure through Spectra X Stream was phenomenal. Professional execution of complex hospitality tech solutions.",
      avatar: "��️"
    }
  ];

  // Compilation steps
  const compilationSteps = [
    "Initializing Lohit's Portfolio System...",
    "Loading React Components...",
    "Compiling project dependencies...",
    "Bundling JavaScript modules...",
    "Processing client testimonials...",
    "Optimizing performance metrics...",
    "Establishing API connections...",
    "Loading project showcase...",
    "Finalizing user interface...",
    "Portfolio compilation complete! 🚀"
  ];

  // Intro animation effect
  useEffect(() => {
    if (!showIntro) return;

    const stepInterval = setInterval(() => {
      setCompilationStep(prev => {
        if (prev < compilationSteps.length - 1) {
          return prev + 1;
        } else {
          // Compilation complete, hide intro after a brief pause
          setTimeout(() => setShowIntro(false), 1000);
          clearInterval(stepInterval);
          return prev;
        }
      });
    }, 800);

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setCompilationProgress(prev => {
        if (prev < 100) {
          return Math.min(prev + Math.random() * 15 + 5, 100);
        }
        return prev;
      });
    }, 200);

    return () => {
      clearInterval(stepInterval);
      clearInterval(progressInterval);
    };
  }, [showIntro, compilationSteps.length]);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);

  // Get Saudi Arabia time
  const getSaudiTime = () => {
    return currentTime.toLocaleString('en-US', {
      timeZone: 'Asia/Riyadh',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get business hours status
  const getBusinessHoursStatus = () => {
    const saudiTime = new Date().toLocaleString('en-US', { timeZone: 'Asia/Riyadh' });
    const hour = new Date(saudiTime).getHours();
    
    if (hour >= 9 && hour <= 18) {
      return { status: 'business', message: 'Business Hours - Quick Response' };
    } else if (hour >= 19 && hour <= 23) {
      return { status: 'evening', message: 'Evening Hours - Available for Urgent Projects' };
    } else {
      return { status: 'after-hours', message: 'After Hours - Will Respond by 9 AM' };
    }
  };

  // Get experience info
  const getExperienceInfo = () => {
    return {
      years: '5+ Years Experience',
      projects: '50+ Projects Completed',
      satisfaction: '100% Client Satisfaction',
      portfolio: '10+ Live Applications',
      techStack: '15+ Technologies Mastered',
      industries: 'Multi-Industry Expertise'
    };
  };

  const handleClose = () => {
    setIsAnimating(true);
    setTerminalState('closed');
    setTimeout(() => {
      setTerminalState('normal');
      setIsAnimating(false);
    }, 2000);
  };

  const handleMinimize = () => {
    setIsAnimating(true);
    if (terminalState === 'minimized') {
      setTerminalState('normal');
    } else {
      setTerminalState('minimized');
    }
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleMaximize = () => {
    setIsAnimating(true);
    if (terminalState === 'maximized') {
      setTerminalState('normal');
    } else {
      setTerminalState('maximized');
    }
    setTimeout(() => setIsAnimating(false), 300);
  };

  return (
    <div 
      className={`App${showPolitorium || showLifework || showHobbyist || showAspirantPrep || showAlshifa || showGymTool || showSpectraXStream || showErpTool || showKiranaTool || showApiHandshaking || showFraserYacht || showLuxuryPresence || showTravelot || showExamPrep || showMaldivesGuide || showShoppingApp ? ' modal-open' : ''}`}
      onClick={() => showSettings && setShowSettings(false)}
    >
      {/* Intro Compilation Animation */}
      {showIntro && (
        <div className="intro-overlay">
          <div className="compilation-terminal">
            <div className="compilation-header">
              <div className="compilation-controls">
                <span className="control-dot red"></span>
                <span className="control-dot yellow"></span>
                <span className="control-dot green"></span>
              </div>
              <div className="compilation-title">Portfolio Compiler v2.0.1</div>
            </div>
            
            <div className="compilation-content">
              <div className="compilation-ascii">
                <pre>{`
██╗      ██████╗ ██╗  ██╗██╗████████╗
██║     ██╔═══██╗██║  ██║██║╚══██╔══╝
██║     ██║   ██║███████║██║   ██║   
██║     ██║   ██║██╔══██║██║   ██║   
███████╗╚██████╔╝██║  ██║██║   ██║   
╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═╝   ╚═╝   
                `}</pre>
              </div>
              
              <div className="compilation-steps">
                {compilationSteps.slice(0, compilationStep + 1).map((step, index) => (
                  <div key={index} className={`step ${index === compilationStep ? 'current' : 'completed'}`}>
                    <span className="step-icon">
                      {index === compilationStep ? '⏳' : '✅'}
                    </span>
                    <span className="step-text">{step}</span>
                  </div>
                ))}
              </div>
              
              <div className="compilation-progress">
                <div className="progress-label">
                  Building portfolio... {Math.round(compilationProgress)}%
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${compilationProgress}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="compilation-footer">
                <span className="typing-cursor">█</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Portfolio Content */}
      {!showIntro && (
        <>
          {/* Terminal Window */}
          <div className={`terminal-window ${terminalState} ${isAnimating ? 'animating' : ''} font-${fontSize} theme-${terminalTheme} ${showScanlines ? 'scanlines' : ''}`}>
            {/* Terminal Header */}
            <div className="terminal-header">
              <div className="terminal-controls">
                <span className="control close" onClick={handleClose} title="Close (reopens in 2s)"></span>
                <span className="control minimize" onClick={handleMinimize} title="Minimize/Restore"></span>
                <span className="control maximize" onClick={handleMaximize} title="Maximize/Restore"></span>
              </div>
              <div className="terminal-title">
                lohit-soundarajan — zsh — 80×24
                <div className="availability-status">
                  <span className="status-dot"></span>
                  Available for new projects
                </div>
              </div>
              <div className="terminal-menu">
                <span onClick={() => setShowSettings(!showSettings)} style={{ cursor: 'pointer', position: 'relative' }}>
                  ⚙️
                  {showSettings && (
                    <div className="settings-dropdown" onClick={e => e.stopPropagation()}>
                      <div className="settings-header">Terminal Settings</div>
                      
                      <div className="settings-section">
                        <label>Font Size:</label>
                        <div className="settings-options">
                          <button 
                            className={`settings-btn ${fontSize === 'small' ? 'active' : ''}`}
                            onClick={() => setFontSize('small')}
                          >
                            Small
                          </button>
                          <button 
                            className={`settings-btn ${fontSize === 'normal' ? 'active' : ''}`}
                            onClick={() => setFontSize('normal')}
                          >
                            Normal
                          </button>
                          <button 
                            className={`settings-btn ${fontSize === 'large' ? 'active' : ''}`}
                            onClick={() => setFontSize('large')}
                          >
                            Large
                          </button>
                        </div>
                      </div>

                      <div className="settings-section">
                        <label>Theme:</label>
                        <div className="settings-options">
                          <button 
                            className={`settings-btn ${terminalTheme === 'dark' ? 'active' : ''}`}
                            onClick={() => setTerminalTheme('dark')}
                          >
                            🌙 Dark
                          </button>
                          <button 
                            className={`settings-btn ${terminalTheme === 'matrix' ? 'active' : ''}`}
                            onClick={() => setTerminalTheme('matrix')}
                          >
                            💚 Matrix
                          </button>
                          <button 
                            className={`settings-btn ${terminalTheme === 'retro' ? 'active' : ''}`}
                            onClick={() => setTerminalTheme('retro')}
                          >
                            📟 Retro
                          </button>
                        </div>
                      </div>

                      <div className="settings-section">
                        <label>Effects:</label>
                        <div className="settings-toggle">
                          <button 
                            className={`settings-btn ${showScanlines ? 'active' : ''}`}
                            onClick={() => setShowScanlines(!showScanlines)}
                          >
                            📺 Scanlines {showScanlines ? 'ON' : 'OFF'}
                          </button>
                          <button 
                            className={`settings-btn ${soundEnabled ? 'active' : ''}`}
                            onClick={() => setSoundEnabled(!soundEnabled)}
                          >
                            🔊 Sound {soundEnabled ? 'ON' : 'OFF'}
                          </button>
                        </div>
                      </div>

                      <div className="settings-footer">
                        <button 
                          className="settings-btn close-btn"
                          onClick={() => setShowSettings(false)}
                        >
                          ✕ Close
                        </button>
                      </div>
                    </div>
                  )}
                </span>
              </div>
            </div>

            {/* Terminal Content */}
            <div className="terminal-content">
              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">whoami</span>
              </div>
              
              <div className="output-section">
                <div className="ascii-art">
                  <pre>{`
██╗      ██████╗ ██╗  ██╗██╗████████╗    ███████╗ ██████╗ ██╗   ██╗███╗   ██╗██████╗  █████╗ ██████╗  █████╗      ██╗ █████╗ ███╗   ██╗
██║     ██╔═══██╗██║  ██║██║╚══██╔══╝    ██╔════╝██╔═══██╗██║   ██║████╗  ██║██╔══██╗██╔══██╗██╔══██╗██╔══██╗     ██║██╔══██╗████╗  ██║
██║     ██║   ██║███████║██║   ██║       ███████╗██║   ██║██║   ██║██╔██╗ ██║██║  ██║███████║██████╔╝███████║     ██║███████║██╔██╗ ██║
██║     ██║   ██║██╔══██║██║   ██║       ╚════██║██║   ██║██║   ██║██║╚██╗██║██║  ██║██╔══██║██╔══██╗██╔══██║██   ██║██╔══██║██║╚██╗██║
███████╗╚██████╔╝██║  ██║██║   ██║       ███████║╚██████╔╝╚██████╔╝██║ ╚████║██████╔╝██║  ██║██║  ██║██║  ██║╚█████╔╝██║  ██║██║ ╚████║
╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═╝   ╚═╝       ╚══════╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═══╝╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚════╝ ╚═╝  ╚═╝╚═╝  ╚═══╝
                  `}</pre>
                </div>
              </div>

              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">cat about.txt</span>
              </div>
              
              <div className="file-content">
                <div className="code-block">
                  <span className="comment">// Developer who codes with passion and purpose</span>
                  <br />
                  <span className="keyword">const</span> <span className="variable">developer</span> <span className="operator">=</span> <span className="bracket">{'{'}</span>
                  <br />
                  <span className="indent">  <span className="property">name</span><span className="operator">:</span> <span className="string">"Lohit Soundarajan"</span><span className="comma">,</span></span>
                  <br />
                  <span className="indent">  <span className="property">mission</span><span className="operator">:</span> <span className="string">"Building efficient, user-driven solutions"</span><span className="comma">,</span></span>
                  <br />
                  <span className="indent">  <span className="property">approach</span><span className="operator">:</span> <span className="string">"Clean, scalable code with love"</span><span className="comma">,</span></span>
                  <br />
                  <span className="indent">  <span className="property">focus</span><span className="operator">:</span> <span className="string">"Delivering seamless digital experiences"</span><span className="comma">,</span></span>
                  <br />
                  <span className="indent">  <span className="property">passion</span><span className="operator">:</span> <span className="string">"Creating solutions that make a difference"</span><span className="comma">,</span></span>
                  <br />
                  <span className="indent">  <span className="property">workStyle</span><span className="operator">:</span> <span className="string">"Every line of code written with care"</span><span className="comma">,</span></span>
                  <br />
                  <span className="indent">  <span className="property">getQuote</span><span className="operator">:</span> <span className="function">() =&gt; &#123;</span></span>
                  <br />
                  <span className="indent2">    <span className="keyword">return</span> <span className="string">"I don't just write code, I craft experiences! 💛"</span></span>
                  <br />
                  <span className="indent">  <span className="bracket">&#125;</span></span>
                  <br />
                  <span className="bracket">{'}'}</span>
                </div>
              </div>

              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">cat philosophy.md</span>
              </div>
              
              <div className="file-content">
                <div className="code-block">
                  <span className="string">"I'm Lohit Soundarajan, a developer dedicated to building efficient, user-driven</span>
                  <br />
                  <span className="string">solutions with clean, scalable code and a focus on delivering seamless digital</span>
                  <br />
                  <span className="string">experiences."</span>
                  <br /><br />
                  <span className="comment">// I believe in:</span>
                  <br />
                  <span className="operator">✨</span> <span className="string">Writing code that tells a story</span>
                  <br />
                  <span className="operator">💝</span> <span className="string">Building with empathy and user-first mindset</span>
                  <br />
                  <span className="operator">🚀</span> <span className="string">Continuous learning and growth</span>
                  <br />
                  <span className="operator">🤝</span> <span className="string">Collaboration and meaningful connections</span>
                </div>
              </div>

              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">cat expertise.json</span>
              </div>
              
              <div className="expertise-section">
                <div className="expertise-header">
                  <span className="comment">{/* My Technical Expertise & Specializations */}// My Technical Expertise & Specializations</span>
                </div>
                
                <div className="expertise-grid">
                  <div className="expertise-card">
                    <div className="expertise-icon">📱</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">Mobile Apps:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">iOS & Android development</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">🌐</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">Web Development:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Frontend magic & Backend power</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">💼</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">Consulting:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Technical strategy & guidance</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">🏢</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">Enterprise Solutions:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">ERP & business systems</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">🤖</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">AI Integration:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Smart solutions with ML</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">☁️</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">Cloud & DevOps:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Scalable architecture</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">🔗</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">API Development:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Seamless integrations</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">🎨</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">UI/UX Design:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Beautiful user experiences</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">⚡</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">Optimization:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Performance & scaling</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">🧠</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">RAG Automation:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">AI-powered document intelligence</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">⛓️</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">Blockchain Development:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Web3 & smart contracts</span>
                      </div>
                    </div>
                  </div>

                  <div className="expertise-card">
                    <div className="expertise-icon">📡</div>
                    <div className="expertise-content">
                      <div className="expertise-label">
                        <span className="keyword">IoT Solutions:</span>
                      </div>
                      <div className="expertise-value">
                        <span className="string">Connected devices & automation</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">cat client-reviews.json</span>
              </div>
              
              <div className="testimonials-section">
                <div className="testimonials-header">
                  <span className="comment">{/* What clients say about working with Lohit */}// What clients say about working with Lohit</span>
                </div>
                
                <div className="testimonial-carousel">
                  <div className="testimonial-card">
                    <div className="testimonial-content">
                      <div className="testimonial-text">
                        <span className="string">"{testimonials[currentTestimonial].text}"</span>
                      </div>
                      
                      <div className="testimonial-rating">
                        {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                          <span key={i} className="star">⭐</span>
                        ))}
                      </div>
                      
                      <div className="testimonial-author">
                        <div className="author-avatar">
                          {testimonials[currentTestimonial].avatar}
                        </div>
                        <div className="author-details">
                          <div className="author-name">
                            <span className="variable">{testimonials[currentTestimonial].name}</span>
                          </div>
                          <div className="author-role">
                            <span className="comment">{testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}</span>
                          </div>
                          <div className="project-type">
                            <span className="keyword">Project:</span> <span className="string">{testimonials[currentTestimonial].project}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="carousel-controls">
                    <button 
                      className="carousel-btn prev"
                      onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                    >
                      ‹
                    </button>
                    
                    <div className="carousel-dots">
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          className={`carousel-dot ${index === currentTestimonial ? 'active' : ''}`}
                          onClick={() => setCurrentTestimonial(index)}
                        />
                      ))}
                    </div>
                    
                    <button 
                      className="carousel-btn next"
                      onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                    >
                      ›
                    </button>
                  </div>
                </div>
              </div>

              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">date && cat current-status.txt</span>
              </div>
              
              <div className="availability-section">
                <div className="availability-header">
                  <span className="comment">{/* Live Availability & Response Times */}// Live Availability & Response Times</span>
                </div>
                
                <div className="status-grid">
                  <div className="status-card timezone-card">
                    <div className="status-icon">🌍</div>
                    <div className="status-content">
                      <div className="status-label">
                        <span className="keyword">Current Time (Saudi Arabia):</span>
                      </div>
                      <div className="status-value">
                        <span className="string">{getSaudiTime()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="status-card response-card">
                    <div className="status-icon">⚡</div>
                    <div className="status-content">
                      <div className="status-label">
                        <span className="keyword">Response Guarantee:</span>
                      </div>
                      <div className="status-value">
                        <span className="string">{getBusinessHoursStatus().message}</span>
                      </div>
                    </div>
                  </div>

                  <div className="status-card experience-card">
                    <div className="status-icon">🎯</div>
                    <div className="status-content">
                      <div className="status-label">
                        <span className="keyword">Experience:</span>
                      </div>
                      <div className="status-value">
                        <span className="string">{getExperienceInfo().years}</span>
                      </div>
                    </div>
                  </div>

                  <div className="status-card satisfaction-card">
                    <div className="status-icon">⭐</div>
                    <div className="status-content">
                      <div className="status-label">
                        <span className="keyword">Track Record:</span>
                      </div>
                      <div className="status-value">
                        <span className="string">{getExperienceInfo().satisfaction}</span>
                      </div>
                    </div>
                  </div>

                  <div className="status-card portfolio-card">
                    <div className="status-icon">🚀</div>
                    <div className="status-content">
                      <div className="status-label">
                        <span className="keyword">Live Portfolio:</span>
                      </div>
                      <div className="status-value">
                        <span className="string">{getExperienceInfo().portfolio}</span>
                      </div>
                    </div>
                  </div>

                  <div className="status-card tech-card">
                    <div className="status-icon">💻</div>
                    <div className="status-content">
                      <div className="status-label">
                        <span className="keyword">Tech Expertise:</span>
                      </div>
                      <div className="status-value">
                        <span className="string">{getExperienceInfo().techStack}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="quick-actions">
                  <div className="action-header">
                    <span className="comment">{/* Quick Actions */}// Quick Actions</span>
                  </div>
                  <div className="action-buttons">
                    <a href="mailto:lohitsoundarajan@gmail.com?subject=Project Inquiry&body=Hi Lohit, I'm interested in discussing a project..." className="action-btn priority">
                      <span className="btn-icon">🚀</span>
                      <span>Start Project Discussion</span>
                    </a>
                    <a href="https://calendly.com/lohitsoundarajan" className="action-btn secondary" target="_blank" rel="noopener noreferrer">
                      <span className="btn-icon">📞</span>
                      <span>Schedule 15-min Call</span>
                    </a>
                    <a href="mailto:lohitsoundarajan@gmail.com" className="action-btn email">
                      <span className="btn-icon">📧</span>
                      <span>Email Direct</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">ls projects/</span>
              </div>
              
              <div className="projects-grid">
                <div className="project-item" onClick={() => setShowPolitorium(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">politorium/</span>
                </div>
                <div className="project-item" onClick={() => setShowLifework(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">lifework/</span>
                </div>
                <div className="project-item" onClick={() => setShowHobbyist(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">hobbyist/</span>
                </div>
                <div className="project-item">
                  <span className="folder-icon">📁</span>
                  <span className="project-name">lyve/</span>
                </div>
                <div className="project-item" onClick={() => setShowAspirantPrep(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">aspirant-prep/</span>
                </div>
                <div className="project-item" onClick={() => setShowAlshifa(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">alshifa-therapy/</span>
                </div>
                <div className="project-item" onClick={() => setShowGymTool(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">gym-tool/</span>
                </div>
                <div className="project-item" onClick={() => setShowSpectraXStream(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">spectra-x-stream/</span>
                </div>
                <div className="project-item" onClick={() => setShowErpTool(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">erp-tool/</span>
                </div>
                <div className="project-item" onClick={() => setShowKiranaTool(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">kirana-tool/</span>
                </div>
                <div className="project-item" onClick={() => setShowApiHandshaking(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">api-handshaking-tool/</span>
                </div>
                <div className="project-item" onClick={() => setShowFraserYacht(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">fraser-yacht/</span>
                </div>
                <div className="project-item" onClick={() => setShowLuxuryPresence(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">luxury-presence/</span>
                </div>
                <div className="project-item" onClick={() => setShowTravelot(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">travelot/</span>
                </div>
                <div className="project-item" onClick={() => setShowExamPrep(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">exam-prep/</span>
                </div>
                <div className="project-item" onClick={() => setShowMaldivesGuide(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">maldives-guide/</span>
                </div>
                <div className="project-item" onClick={() => setShowShoppingApp(true)} style={{cursor:'pointer'}}>
                  <span className="folder-icon">📁</span>
                  <span className="project-name">shopping-app/</span>
                </div>
                <div className="project-item" style={{opacity: 0.7, fontStyle: 'italic'}}>
                  <span className="folder-icon">✨</span>
                  <span className="project-name">and much more...</span>
                </div>
              </div>

              <div className="terminal-line">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command">./connect-with-me.sh</span>
              </div>
              
              <div className="contact-section">
                <div className="contact-buttons">
                  <a href="mailto:lohitsoundarajan@gmail.com" className="terminal-button" data-tooltip="lohitsoundarajan@gmail.com">
                    <span className="button-icon">📧</span>
                    <span>Email</span>
                  </a>
                  <a href="https://paypal.me/lohit2127" className="terminal-button" data-tooltip="PayPal: lohit2127">
                    <span className="button-icon">💳</span>
                    <span>PayPal</span>
                  </a>
                  <a href="https://linkedin.com/in/lohitsoundarajan" className="terminal-button" data-tooltip="linkedin.com/in/lohitsoundarajan">
                    <span className="button-icon">💼</span>
                    <span>LinkedIn</span>
                  </a>
                  <a href="https://x.com/nerdygeek2127" className="terminal-button" data-tooltip="@nerdygeek2127" target="_blank" rel="noopener noreferrer">
                    <span className="button-icon">🐦</span>
                    <span>Twitter</span>
                  </a>
                </div>
              </div>

              <div className="terminal-line current">
                <span className="prompt">➜</span>
                <span className="directory">~</span>
                <span className="command typing"></span>
                <span className="cursor">█</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Politorium Popup Modal */}
      {showPolitorium && (
        <div className="modal-overlay" onClick={() => setShowPolitorium(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowPolitorium(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <img
                  src="/politorium.png"
                  alt="Politorium App Screenshot"
                  className="modal-image no-shadow side-image"
                />
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Politorium</h2>
                <p className="modal-tagline">The Social Platform for Respectful Political Discourse</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Politorium</strong> is a next-generation platform designed to foster meaningful, respectful political conversations. Built for those passionate about positive change, it empowers users to:
                  </p>
                  <ul className="modal-features">
                    <li><strong>Respectful Dialogue:</strong> Foster meaningful conversations across political divides.</li>
                    <li><strong>Break Echo Chambers:</strong> Engage with diverse perspectives through AI-guided discussions.</li>
                    <li><strong>Community First:</strong> Join a platform built on mutual respect and continuous learning.</li>
                    <li><strong>Transparent Moderation:</strong> Experience fair and open moderation with community involvement.</li>
                    <li><strong>Reputation System:</strong> Gain recognition for thoughtful, respectful engagement.</li>
                  </ul>
                  <div className="modal-availability">Available on iOS & Web</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://www.politorium.com/"
                    className="modal-link-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
                    Website
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/politorium/id6740051311"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    App Store
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LifeWork Popup Modal */}
      {showLifework && (
        <div className="modal-overlay" onClick={() => setShowLifework(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowLifework(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <img
                  src="/lifework.jpg"
                  alt="LifeWork Website Screenshot"
                  className="modal-image no-shadow side-image"
                />
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>LifeWork</h2>
                <p className="modal-tagline">AI-Powered Personal & Professional Growth</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>LifeWork</strong> empowers individuals to navigate personal and professional growth using AI-driven insights. This innovative platform combines cutting-edge technology with personalized guidance to help users achieve their goals.
                  </p>
                  <div className="modal-availability">Available on Web</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://lifework.live/"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hobbyist Popup Modal */}
      {showHobbyist && (
        <div className="modal-overlay" onClick={() => setShowHobbyist(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowHobbyist(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <img
                  src="/hobbyist.jpg"
                  alt="Hobbyist App Screenshot"
                  className="modal-image no-shadow side-image"
                />
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Hobbyist</h2>
                <p className="modal-tagline">Discover & Connect Through Events</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Hobbyist</strong> is an innovative events-based platform that connects people through shared interests and activities. Whether you're looking to discover new hobbies or connect with like-minded individuals, Hobbyist makes it easy to:
                  </p>
                  <ul className="modal-features">
                    <li><strong>Event Discovery:</strong> Find exciting events and activities in your area.</li>
                    <li><strong>Community Building:</strong> Connect with people who share your passions and interests.</li>
                    <li><strong>Hobby Exploration:</strong> Discover new hobbies and expand your horizons.</li>
                    <li><strong>Social Networking:</strong> Build meaningful relationships through shared experiences.</li>
                    <li><strong>Easy Participation:</strong> Join events seamlessly with intuitive booking and management.</li>
                  </ul>
                  <div className="modal-availability">Available on Web</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://hobbyist.co/"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AspirantPrep Popup Modal */}
      {showAspirantPrep && (
        <div className="modal-overlay" onClick={() => setShowAspirantPrep(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowAspirantPrep(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>🎓</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>Coming Soon</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>AspirantPrep</h2>
                <p className="modal-tagline">AI-Powered Competitive Exam Preparation</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>AspirantPrep</strong> is designed for users preparing for competitive exams in India. This intelligent platform leverages AI technology to create personalized questions based on syllabus requirements and previous year papers, helping aspirants excel in their preparation journey.
                  </p>
                  <ul className="modal-features">
                    <li><strong>AI-Generated Questions:</strong> Intelligent question creation based on exam patterns.</li>
                    <li><strong>Syllabus-Based Content:</strong> Comprehensive coverage of all syllabus topics.</li>
                    <li><strong>Previous Year Analysis:</strong> Questions derived from historical exam data.</li>
                    <li><strong>Personalized Learning:</strong> Adaptive learning paths for individual needs.</li>
                    <li><strong>Performance Tracking:</strong> Detailed analytics to monitor progress.</li>
                  </ul>
                  <div className="modal-availability" style={{color: '#ffcc02'}}>App in Development</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alshifa Popup Modal */}
      {showAlshifa && (
        <div className="modal-overlay" onClick={() => setShowAlshifa(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowAlshifa(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <img
                  src="/alshifa.jpg"
                  alt="Alshifa App Screenshot"
                  className="modal-image no-shadow side-image"
                />
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Alshifa</h2>
                <p className="modal-tagline">Your Path to Mental Wellness Through Faith</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Alshifa</strong> integrates Islamic principles with modern psychological science to provide a unique approach to mental wellness. Using our Deen as a foundation for healing and balance, this platform offers:
                  </p>
                  <ul className="modal-features">
                    <li><strong>Faith-Based Healing:</strong> Combining Islamic teachings with psychological therapy.</li>
                    <li><strong>Deen Foundation:</strong> Using religious principles as the cornerstone for mental wellness.</li>
                    <li><strong>Family Relationships:</strong> Building strong families through faith-centered approaches.</li>
                    <li><strong>Holistic Balance:</strong> Achieving emotional and spiritual equilibrium through Islam.</li>
                    <li><strong>Community Support:</strong> Connecting with others on similar faith-based wellness journeys.</li>
                  </ul>
                  <div className="modal-availability">Available for Islamic Community</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gym Tool Popup Modal */}
      {showGymTool && (
        <div className="modal-overlay" onClick={() => setShowGymTool(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowGymTool(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image gym-placeholder">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>🏋️</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>Gym ERP System</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Gym ERP Tool</h2>
                <p className="modal-tagline">Complete Gym Management Solution</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Gym ERP India</strong> revolutionizes fitness center operations with a comprehensive management platform. Designed by fitness professionals and tech experts, this all-in-one solution streamlines gym administration and enhances member experience.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Membership Management:</strong> Automated tracking, renewals, and payment processing.</li>
                    <li><strong>Biometric Authentication:</strong> Advanced face recognition system for secure access.</li>
                    <li><strong>QR Code Technology:</strong> Quick and contactless member check-ins.</li>
                    <li><strong>Class Scheduling:</strong> Drag-and-drop functionality for easy class management.</li>
                    <li><strong>WhatsApp Integration:</strong> Automated member communication and reminders.</li>
                    <li><strong>Mobile App:</strong> Member and admin apps for iOS and Android platforms.</li>
                    <li><strong>Payment Gateway:</strong> Multiple payment options with automated billing.</li>
                    <li><strong>Analytics Dashboard:</strong> Real-time insights and performance tracking.</li>
                  </ul>
                  <div className="modal-availability">Live System - 5000+ Clients</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://gymerpindia.com/"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spectra X Stream Popup Modal */}
      {showSpectraXStream && (
        <div className="modal-overlay" onClick={() => setShowSpectraXStream(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowSpectraXStream(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <img
                  src="/spectra.jpg"
                  alt="Spectra X Stream ELV Solutions"
                  className="modal-image no-shadow side-image"
                />
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Spectra X Stream</h2>
                <p className="modal-tagline">ELV & Smart Technology Solutions for Hospitality</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Spectra X Stream</strong> is pioneering "Man Weds Machine" technology solutions, connecting humans and technology through intelligent network infrastructure. Specializing in ELV (Extra Low Voltage) systems for luxury resorts and hospitality properties across the Maldives and beyond.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Network Infrastructure:</strong> Next-generation GPON and fiber optic solutions for resorts.</li>
                    <li><strong>Hospitality Technologies:</strong> Integrated systems for luxury hotels and resorts.</li>
                    <li><strong>Smart City Solutions:</strong> Advanced IoT and connectivity infrastructure.</li>
                    <li><strong>ELV Systems:</strong> Complete extra low voltage installations and integration.</li>
                    <li><strong>Communication & Mobility:</strong> Seamless wireless and data communication systems.</li>
                    <li><strong>Integrated Security:</strong> Comprehensive security and access control solutions.</li>
                    <li><strong>Project Portfolio:</strong> Anantara Kihavah, Waldorf Astoria, Grand Park Kodhipparu.</li>
                    <li><strong>Road to 2030:</strong> Vision for making Maldives a digital paradise.</li>
                  </ul>
                  <div className="modal-availability">Leading ELV Contractor - Maldives & Global</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://spectraxstream.com/"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ERP Tool Popup Modal */}
      {showErpTool && (
        <div className="modal-overlay" onClick={() => setShowErpTool(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowErpTool(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>🏢</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>Enterprise System</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>ERP Tool</h2>
                <p className="modal-tagline">Enterprise Resource Planning Solution</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>ERP Tool</strong> is a comprehensive enterprise resource planning solution designed to streamline business operations and integrate various organizational processes. This powerful platform helps businesses optimize their workflow, manage resources efficiently, and make data-driven decisions.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Financial Management:</strong> Complete accounting, invoicing, and financial reporting.</li>
                    <li><strong>Human Resources:</strong> Employee management, payroll, and performance tracking.</li>
                    <li><strong>Inventory Management:</strong> Real-time stock tracking and automated reordering.</li>
                    <li><strong>Customer Relations:</strong> CRM integration for enhanced customer engagement.</li>
                    <li><strong>Supply Chain:</strong> Vendor management and procurement optimization.</li>
                    <li><strong>Business Intelligence:</strong> Advanced analytics and customizable dashboards.</li>
                    <li><strong>Multi-Branch Support:</strong> Centralized management for multiple locations.</li>
                    <li><strong>Security & Compliance:</strong> Role-based access and regulatory compliance.</li>
                  </ul>
                  <div className="modal-availability">Enterprise-Grade Solution</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kirana Tool Popup Modal */}
      {showKiranaTool && (
        <div className="modal-overlay" onClick={() => setShowKiranaTool(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowKiranaTool(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>🏪</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>Local Store Management</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Kirana Tool</h2>
                <p className="modal-tagline">Smart Management for Local Grocery Stores</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Kirana Tool</strong> is specifically designed for small and medium grocery stores (kirana shops) in India. This intuitive platform helps local shop owners modernize their operations, manage inventory efficiently, and serve customers better while maintaining the personal touch of neighborhood stores.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Inventory Tracking:</strong> Real-time stock management with low-stock alerts.</li>
                    <li><strong>Billing System:</strong> Quick and easy point-of-sale with receipt generation.</li>
                    <li><strong>Customer Credits:</strong> Track customer accounts and credit transactions.</li>
                    <li><strong>Supplier Management:</strong> Maintain vendor records and purchase orders.</li>
                    <li><strong>Sales Analytics:</strong> Daily, weekly, and monthly sales reporting.</li>
                    <li><strong>Multi-Language Support:</strong> Interface in local Indian languages.</li>
                    <li><strong>Mobile-Friendly:</strong> Works on smartphones for easy access.</li>
                    <li><strong>Offline Mode:</strong> Continues working even with poor internet connectivity.</li>
                  </ul>
                  <div className="modal-availability">Tailored for Indian Kirana Stores</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Handshaking Tool Popup Modal */}
      {showApiHandshaking && (
        <div className="modal-overlay" onClick={() => setShowApiHandshaking(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowApiHandshaking(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>🤝</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>API Integration</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>API Handshaking Tool</h2>
                <p className="modal-tagline">Streamlined API Integration & Testing Platform</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>API Handshaking Tool</strong> is a comprehensive development utility designed to simplify API integration processes. This tool enables developers to test, debug, and integrate APIs efficiently, reducing development time and ensuring robust connections between different services and applications.
                  </p>
                  <ul className="modal-features">
                    <li><strong>API Testing:</strong> Comprehensive endpoint testing with various HTTP methods.</li>
                    <li><strong>Authentication Support:</strong> Multiple auth types including OAuth, JWT, and API keys.</li>
                    <li><strong>Request Builder:</strong> Visual interface for constructing API requests easily.</li>
                    <li><strong>Response Analysis:</strong> Detailed response inspection and validation tools.</li>
                    <li><strong>Documentation Generator:</strong> Auto-generate API documentation from tests.</li>
                    <li><strong>Environment Management:</strong> Switch between dev, staging, and production environments.</li>
                    <li><strong>Collaboration:</strong> Share API collections and tests with team members.</li>
                    <li><strong>Integration Testing:</strong> Automated testing workflows and continuous integration.</li>
                  </ul>
                  <div className="modal-availability">Developer Tool - Open Source</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fraser Yacht Popup Modal */}
      {showFraserYacht && (
        <div className="modal-overlay" onClick={() => setShowFraserYacht(false)}>
          <div className="modal-content modern-politorium-modal side-modal iframe-large" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowFraserYacht(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner iframe-layout">
              <div className="iframe-container">
                <iframe
                  src="https://www.fraseryachts.com/"
                  className="desktop-view"
                  title="Fraser Yachts Website"
                  allowFullScreen
                />
              </div>
              <div className="iframe-content">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Fraser Yacht</h2>
                <p className="modal-tagline">World's Leading Luxury Yacht Brokerage</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Fraser Yachts</strong> is the world's leading yacht brokerage company with over 75 years of experience. Managing a portfolio worth $7.4+ billion, Fraser provides comprehensive luxury yacht services to ultra-high-net-worth individuals globally.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Yacht Portfolio:</strong> $7.4B+ in managed superyachts worldwide.</li>
                    <li><strong>Global Presence:</strong> 230+ specialists across 20 international offices.</li>
                    <li><strong>Charter Excellence:</strong> 6,500+ charter days annually in premium destinations.</li>
                    <li><strong>Full-Service Platform:</strong> Sales, charter, management, and crew placement.</li>
                    <li><strong>Luxury Fleet:</strong> 80m-100m+ superyachts from top shipyards.</li>
                    <li><strong>Elite Clientele:</strong> 13,000+ active qualified clients globally.</li>
                  </ul>
                  <div className="modal-availability">Live Platform - Global Leader</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://www.fraseryachts.com/"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Luxury Presence Popup Modal */}
      {showLuxuryPresence && (
        <div className="modal-overlay" onClick={() => setShowLuxuryPresence(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowLuxuryPresence(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <img
                  src="/luxury.jpg"
                  alt="Luxury Presence Platform Screenshot"
                  className="modal-image no-shadow side-image"
                />
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Luxury Presence</h2>
                <p className="modal-tagline">Premium Real Estate Digital Platform</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Luxury Presence</strong> revolutionizes high-end real estate marketing through cutting-edge digital solutions. This platform empowers luxury real estate professionals with sophisticated websites, marketing tools, and client management systems designed specifically for the luxury market.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Luxury Websites:</strong> Bespoke real estate websites for high-end properties.</li>
                    <li><strong>Advanced CRM:</strong> Client relationship management for luxury market.</li>
                    <li><strong>Marketing Automation:</strong> Sophisticated lead generation and nurturing.</li>
                    <li><strong>Property Showcase:</strong> Immersive virtual tours and galleries.</li>
                    <li><strong>Market Analytics:</strong> Luxury market insights and reporting.</li>
                    <li><strong>Brand Building:</strong> Personal branding tools for luxury agents.</li>
                  </ul>
                  <div className="modal-availability">Enterprise Real Estate Solution</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://www.luxurypresence.com/"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Travelot Popup Modal */}
      {showTravelot && (
        <div className="modal-overlay" onClick={() => setShowTravelot(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowTravelot(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>✈️</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>Travel & Tourism</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Travelot</h2>
                <p className="modal-tagline">Comprehensive Tourism Service Platform</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Travelot</strong> is a comprehensive tourism service platform designed to revolutionize travel planning and booking experiences. This mobile application provides travelers with seamless access to accommodations, tours, local experiences, and travel services all in one place.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Trip Planning:</strong> Complete itinerary management and booking system.</li>
                    <li><strong>Local Experiences:</strong> Curated tours and activities with local guides.</li>
                    <li><strong>Accommodation Booking:</strong> Hotels, resorts, and unique stays.</li>
                    <li><strong>Transportation:</strong> Flight bookings, car rentals, and transfers.</li>
                    <li><strong>Travel Guides:</strong> Detailed destination information and recommendations.</li>
                    <li><strong>Real-time Support:</strong> 24/7 customer assistance during travels.</li>
                  </ul>
                  <div className="modal-availability">Available on Google Play Store</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.app.pk.travelot"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download App
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exam Prep Popup Modal */}
      {showExamPrep && (
        <div className="modal-overlay" onClick={() => setShowExamPrep(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowExamPrep(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>📚</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>Education & Testing</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Exam Prep - GRE Practice</h2>
                <p className="modal-tagline">Comprehensive GRE Preparation Platform</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Exam Prep</strong> is a specialized mobile application designed for GRE (Graduate Record Examination) preparation. This comprehensive platform provides students with extensive practice tests, detailed analytics, and personalized study plans to excel in their graduate school entrance exams.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Practice Tests:</strong> Full-length GRE simulation exams with accurate timing.</li>
                    <li><strong>Adaptive Learning:</strong> AI-powered question selection based on performance.</li>
                    <li><strong>Detailed Analytics:</strong> Comprehensive score reports and improvement tracking.</li>
                    <li><strong>Study Plans:</strong> Personalized preparation schedules based on target scores.</li>
                    <li><strong>Question Bank:</strong> Thousands of practice questions across all GRE sections.</li>
                    <li><strong>Performance Insights:</strong> Weakness identification and targeted practice.</li>
                  </ul>
                  <div className="modal-availability">Available on Google Play Store</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.bestfungames.shrm"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download App
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maldives Guide Popup Modal */}
      {showMaldivesGuide && (
        <div className="modal-overlay" onClick={() => setShowMaldivesGuide(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowMaldivesGuide(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>🏝️</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>Travel Guide</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>Maldives Travel Guide</h2>
                <p className="modal-tagline">Complete Paradise Island Companion</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>Maldives Travel Guide</strong> is the ultimate companion app for exploring the Maldives archipelago. This comprehensive guide provides travelers with detailed information about resorts, activities, local culture, and essential travel tips for experiencing paradise in the Indian Ocean.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Resort Directory:</strong> Complete guide to luxury resorts and accommodations.</li>
                    <li><strong>Activities Guide:</strong> Snorkeling, diving, water sports, and island hopping.</li>
                    <li><strong>Cultural Insights:</strong> Local customs, traditions, and cultural experiences.</li>
                    <li><strong>Travel Planning:</strong> Seasonal guides, weather information, and best times to visit.</li>
                    <li><strong>Photography Spots:</strong> Best locations for capturing stunning tropical scenery.</li>
                    <li><strong>Offline Access:</strong> Essential information available without internet connection.</li>
                  </ul>
                  <div className="modal-availability">Available on Google Play Store</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.etips.maldives.travel.guide"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download App
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shopping App Popup Modal */}
      {showShoppingApp && (
        <div className="modal-overlay" onClick={() => setShowShoppingApp(false)}>
          <div className="modal-content modern-politorium-modal side-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-x-close" onClick={() => setShowShoppingApp(false)} aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="6" y1="6" x2="16" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="6" x2="6" y2="16" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="side-modal-inner">
              <div className="side-modal-image-wrap">
                <div className="placeholder-image">
                  <span style={{fontSize: '4rem', color: '#4ec9b0'}}>🛒</span>
                  <p style={{color: '#bdbdbd', marginTop: '10px', fontSize: '0.9rem'}}>E-commerce Platform</p>
                </div>
              </div>
              <div className="side-modal-content-wrap">
                <h2 style={{marginBottom: 8, marginTop: 0, fontSize: '1.8rem'}}>FluxStore Shopping App</h2>
                <p className="modal-tagline">Modern E-commerce Mobile Experience</p>
                <div className="modal-extra">
                  <p className="modal-desc">
                    <strong>FluxStore Shopping App</strong> is a comprehensive e-commerce platform built with Flutter, offering a seamless shopping experience across multiple product categories. This modern mobile application combines elegant design with powerful functionality to deliver exceptional user engagement and conversion rates.
                  </p>
                  <ul className="modal-features">
                    <li><strong>Multi-Category Store:</strong> Fashion, electronics, home goods, and lifestyle products.</li>
                    <li><strong>Advanced Search:</strong> AI-powered product discovery and filtering options.</li>
                    <li><strong>Secure Payments:</strong> Multiple payment gateways with wallet integration.</li>
                    <li><strong>Wishlist & Cart:</strong> Advanced shopping cart with save-for-later functionality.</li>
                    <li><strong>Order Tracking:</strong> Real-time delivery updates and order management.</li>
                    <li><strong>Social Features:</strong> Product reviews, ratings, and social sharing.</li>
                  </ul>
                  <div className="modal-availability">Available on Google Play Store</div>
                </div>
                <div className="modal-links-row">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.eshop.fluxstore"
                    className="modal-link-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download App
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
